import { PermissionGate } from "@/components/common/PermissionGate"
import { Button } from "@/components/ui/button"
import { PERMISSIONS } from "@/constants/permissions"
import { SETTINGS_PATHS } from "@/features/settings/paths"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { Settings, UploadCloud } from "lucide-react"
import { Navigate, useNavigate, useSearchParams } from "react-router"
import type { SupplierInquiryListItem } from "../../../api/supplierInquiriesApi"
import { MasterDataPanelLayout } from "../../master-data/MasterDataPanelLayout"
import { useRowMultiSelect } from "../../master-data/useRowMultiSelect"
import { ModuleHeader } from "../../shared/ModuleHeader"
import { InquiryDetailPanel } from "../inquiry/InquiryDetailPanel"
import { InquiryListPanel } from "../inquiry/InquiryListPanel"
import { legacyInquirySettingsPath } from "../inquiry/routes"

/** Query param of the former template-settings dialog, see
 *  legacyInquirySettingsPath. */
const SETTINGS_PARAM = "settings"

export function InquiryPage() {
  const { translate } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const legacySettingsSection = searchParams.get(SETTINGS_PARAM)
  // Typed with the row: a checked draft is remembered WITH the row it came
  // from, so the bulk panel renders the selection without resolving it again.
  const {
    selectedIds,
    selectedRows,
    toggleSelected,
    removeIds,
    selectAll,
    clearAll,
  } = useRowMultiSelect<SupplierInquiryListItem>()

  if (legacySettingsSection !== null) {
    return (
      <Navigate to={legacyInquirySettingsPath(legacySettingsSection)} replace />
    )
  }

  return (
    <div
      data-agent-target="inquiry.list"
      className="flex h-full flex-col gap-4"
    >
      <ModuleHeader
        title={translate("MC_SUPL_TITLE")}
        helpArticle={{
          categoryId: "supplier-studio",
          articleId: "abfragen-verfolgen",
        }}
        subtitle={translate("MC_SUPL_SUBTITLE")}
        right={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              className="h-[30px] w-[30px]"
              aria-label={translate("SETTINGS/SUPPLIER_INQUIRIES/OPEN")}
              onClick={() => navigate(SETTINGS_PATHS.supplierInquiries)}
            >
              <Settings size={15} />
            </Button>
            <PermissionGate permission={PERMISSIONS.MC_INQUIRIES_SEND}>
              <Button
                variant="default"
                size="sm"
                className="h-[30px] gap-1.5 bg-yellow1 text-[12.5px] text-black hover:bg-yellow2"
                onClick={() => navigate("new")}
              >
                <UploadCloud size={13} />
                {translate("MC_NEW_INQRY_BTN")}
              </Button>
            </PermissionGate>
          </div>
        }
      />

      <div className="min-h-0 flex-1">
        <MasterDataPanelLayout
          listPanel={
            <InquiryListPanel
              selectedIds={selectedIds}
              onToggleSelected={toggleSelected}
              onSelectAll={selectAll}
              onClearAll={clearAll}
            />
          }
          detailPanel={
            <InquiryDetailPanel
              selectedRows={selectedRows}
              onRemoveIds={removeIds}
              onClearSelection={clearAll}
            />
          }
        />
      </div>
    </div>
  )
}
